import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import TraccarSidebar from './TraccarSidebar';
import MapView from './MapView';
import SettingsLayout from './settings/SettingsLayout';
import ReportsLayout from './reports/ReportsLayout';
import { useTraccarSocket } from './useTraccarSocket';
import traccarApi from '../../services/traccarApi';

const MAX_TRAIL_POINTS = 200;

function buildInitialHistory(positions, deviceMap) {
  const history = {};
  for (const pos of positions) {
    if (!pos.latitude || !pos.longitude) continue;

    const device = deviceMap.get(pos.deviceId);
    if (device?.status !== 'online') continue;
    history[pos.deviceId] = [
      {
        lat: pos.latitude,
        lng: pos.longitude,
        timestamp:
          pos.fixTime ||
          pos.deviceTime ||
          pos.serverTime ||
          new Date().toISOString(),
      },
    ];
  }

  return history;
}

function useThrottledState(initialValue, ms) {
  const [state, setState] = useState(initialValue);
  const pendingRef = useRef(null);
  const timerRef = useRef(null);
  const lastCallRef = useRef(0);

  const setThrottled = useCallback(
    (updater) => {
      pendingRef.current = updater;
      const now = Date.now();
      const remaining = ms - (now - lastCallRef.current);

      if (remaining <= 0) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }

        lastCallRef.current = now;

        setState((prev) => {
          const next =
            typeof pendingRef.current === 'function'
              ? pendingRef.current(prev)
              : pendingRef.current;

          pendingRef.current = null;
          return next;
        });
      } else if (!timerRef.current) {
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          lastCallRef.current = Date.now();

          setState((prev) => {
            const next =
              typeof pendingRef.current === 'function'
                ? pendingRef.current(prev)
                : pendingRef.current;

            pendingRef.current = null;
            return next;
          });
        }, remaining);
      }
    },
    [ms]
  );

  return [state, setThrottled];
}

const IntegratedTraccarView = ({ email, password }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mapData, setMapDataThrottled] = useThrottledState(
    { devices: [], positions: [], positionHistory: {} },
    500
  );

  const [appData, setAppData] = useState({
    groups: {},
    loading: true,
    sessionReady: false,
  });

  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [sharedDeviceId, setSharedDeviceId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [serviceCode, setServiceCode] = useState(null);

  const devicesRef = useRef([]);
  const deviceMapRef = useRef(new Map());
  const positionsRef = useRef([]);
  const positionHistoryRef = useRef({});
  const sharedDeviceIdRef = useRef(null);

  const socketInitializedRef = useRef(false);
  const initialDeviceCountRef = useRef(0);

  const traccarEmail = email || import.meta.env.VITE_TRACCAR_USER;
  const traccarPassword = password || import.meta.env.VITE_TRACCAR_PASSWORD;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const serviceCodeParam = urlParams.get('serviceCode');
    if (serviceCodeParam) {
      setServiceCode(serviceCodeParam);
    }

    const pathParts = window.location.pathname.split('/');
    const deviceIndex = pathParts.indexOf('device');

    if (deviceIndex !== -1 && pathParts[deviceIndex + 1]) {
      const id = Number(pathParts[deviceIndex + 1]);

      if (!isNaN(id)) {
        sharedDeviceIdRef.current = id;
        setSharedDeviceId(id);
        setSelectedDeviceId(id);
      }
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      if (!traccarEmail || !traccarPassword) {
        setErrorMessage(
          'No se proporcionaron credenciales. Contacta al administrador.'
        );
        setAppData((s) => ({ ...s, loading: false }));
        return;
      }

      try {
        await traccarApi.login(traccarEmail, traccarPassword);
      } catch {
        setErrorMessage(
          'Error al iniciar sesión. Verifica tus credenciales.'
        );
        setAppData((s) => ({ ...s, loading: false }));
        return;
      }

      if (!mounted) return;

      let devicesData;
      let positionsData;

      try {
        [devicesData, positionsData] = await Promise.all([
          traccarApi.getDevices(),
          traccarApi.getPositions(),
        ]);
      } catch (err) {
        console.error('[Traccar] fetch error:', err);

        if (mounted) {
          setErrorMessage(
            'Error al conectar con Traccar. Revisa la consola.'
          );
          setAppData((s) => ({ ...s, loading: false }));
        }

        return;
      }

      if (!mounted) return;

      const sid = sharedDeviceIdRef.current;
      const serviceCodeParam = serviceCode;

      let filteredDevices = sid
        ? devicesData.filter((d) => d.id === sid)
        : devicesData;

      let filteredPositions = sid
        ? positionsData.filter((p) => p.deviceId === sid)
        : positionsData;

      if (serviceCodeParam && !sid) {
        const devicesWithService = [];
        const positionsWithService = [];

        for (const pos of positionsData) {
          try {
            const raw = pos?.attributes?.service_info;
            const info = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {};
            if (info.service_code === serviceCodeParam) {
              positionsWithService.push(pos);
              const device = devicesData.find(d => d.id === pos.deviceId);
              if (device && !devicesWithService.find(d => d.id === device.id)) {
                devicesWithService.push(device);
              }
            }
          } catch (_) {}
        }

        if (devicesWithService.length > 0) {
          filteredDevices = devicesWithService;
          filteredPositions = positionsWithService;
          setSelectedDeviceId(devicesWithService[0]?.id);
        }
      }

      const deviceMap = new Map(filteredDevices.map((d) => [d.id, d]));
      const initialHistory = buildInitialHistory(
        filteredPositions,
        deviceMap
      );

      devicesRef.current = filteredDevices;
      deviceMapRef.current = new Map(
        filteredDevices.map((d) => [d.id, d])
      );
      positionsRef.current = filteredPositions;
      positionHistoryRef.current = initialHistory;

      initialDeviceCountRef.current = filteredDevices.length;
      socketInitializedRef.current = false;

      setMapDataThrottled({
        devices: filteredDevices,
        positions: filteredPositions,
        positionHistory: initialHistory,
      });

      setAppData((s) => ({
        ...s,
        sessionReady: true,
        loading: false,
      }));

      traccarApi
        .getGroups()
        .then((groupsData) => {
          if (!mounted) return;

          const groupsObj = groupsData.reduce((acc, g) => {
            acc[g.id] = g;
            return acc;
          }, {});

          setAppData((s) => ({ ...s, groups: groupsObj }));
        })
        .catch((err) =>
          console.warn('[Traccar] groups fetch error:', err)
        );
    };

    init();

    return () => {
      mounted = false;
    };
  }, [traccarEmail, traccarPassword, serviceCode]);

  const handleSocketMessage = useCallback(
    (data) => {
      const sid = sharedDeviceIdRef.current;
      const serviceCodeParam = serviceCode;

      if (!socketInitializedRef.current) {
        const batchDevices = data.devices?.length ?? 0;
        const batchPositions = data.positions?.length ?? 0;
        const threshold = initialDeviceCountRef.current * 0.9;

        if (batchDevices >= threshold || batchPositions >= threshold) {
          socketInitializedRef.current = true;
          return;
        }

        socketInitializedRef.current = true;
      }

      let newDevices = null;
      let newPositions = null;
      let historyChanged = false;

      if (data.devices?.length) {
        let incoming = sid
          ? data.devices.filter((d) => d.id === sid)
          : data.devices;

        if (serviceCodeParam && !sid) {
          incoming = incoming.filter(device => {
            const devicePositions = positionsRef.current.filter(p => p.deviceId === device.id);
            return devicePositions.some(pos => {
              try {
                const raw = pos?.attributes?.service_info;
                const info = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {};
                return info.service_code === serviceCodeParam;
              } catch {
                return false;
              }
            });
          });
        }

        if (incoming.length) {
          const devMap = new Map(
            devicesRef.current.map((d) => [d.id, d])
          );

          incoming.forEach((d) =>
            devMap.set(d.id, { ...devMap.get(d.id), ...d })
          );

          newDevices = Array.from(devMap.values());
          devicesRef.current = newDevices;
          deviceMapRef.current = new Map(
            newDevices.map((d) => [d.id, d])
          );
        }
      }

      if (data.positions?.length) {
        let incoming = sid
          ? data.positions.filter((p) => p.deviceId === sid)
          : data.positions;

        if (serviceCodeParam && !sid) {
          incoming = incoming.filter(pos => {
            try {
              const raw = pos?.attributes?.service_info;
              const info = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {};
              return info.service_code === serviceCodeParam;
            } catch {
              return false;
            }
          });
        }

        if (incoming.length) {
          const posMap = new Map(
            positionsRef.current.map((p) => [p.deviceId, p])
          );

          incoming.forEach((p) =>
            posMap.set(p.deviceId, {
              ...posMap.get(p.deviceId),
              ...p,
            })
          );

          newPositions = Array.from(posMap.values());
          positionsRef.current = newPositions;

          for (const pos of incoming) {
            if (!pos.latitude || !pos.longitude) continue;

            const device = deviceMapRef.current.get(pos.deviceId);
            if (device?.status !== 'online') continue;

            const existing =
              positionHistoryRef.current[pos.deviceId] || [];

            const lastPoint = existing[existing.length - 1];

            const newTime = new Date(
              pos.fixTime ||
                pos.deviceTime ||
                pos.serverTime ||
                0
            ).getTime();

            const lastTime = lastPoint
              ? new Date(lastPoint.timestamp).getTime()
              : -1;

            if (newTime > lastTime) {
              const newPoint = {
                lat: pos.latitude,
                lng: pos.longitude,
                timestamp:
                  pos.fixTime ||
                  pos.deviceTime ||
                  pos.serverTime ||
                  new Date().toISOString(),
              };

              positionHistoryRef.current = {
                ...positionHistoryRef.current,
                [pos.deviceId]: [
                  ...existing.slice(-(MAX_TRAIL_POINTS - 1)),
                  newPoint,
                ],
              };

              historyChanged = true;
            }
          }
        }
      }

      if (newDevices || newPositions || historyChanged) {
        setMapDataThrottled((prev) => ({
          devices: newDevices ?? prev.devices,
          positions: newPositions ?? prev.positions,
          positionHistory: historyChanged
            ? positionHistoryRef.current
            : prev.positionHistory,
        }));
      }
    },
    [setMapDataThrottled, serviceCode]
  );

  useTraccarSocket(
    appData.sessionReady ? traccarEmail : null,
    appData.sessionReady ? traccarPassword : null,
    handleSocketMessage
  );

  const handleDeviceSelect = useCallback(
    (id) => {
      setSelectedDeviceId(id);
      if (isMobile) setSidebarOpen(false);
    },
    [isMobile]
  );

  const handleSettingsClick = useCallback(
    () => setSettingsOpen(true),
    []
  );

  const handleSettingsClose = useCallback(
    () => setSettingsOpen(false),
    []
  );

  const handleReportsClick = useCallback(
    () => setReportsOpen(true),
    []
  );

  const handleReportsClose = useCallback(
    () => setReportsOpen(false),
    []
  );

  if (settingsOpen) return <SettingsLayout onClose={handleSettingsClose} />;
  if (reportsOpen)
    return (
      <ReportsLayout
        onClose={handleReportsClose}
        devices={mapData.devices}
        positions={mapData.positions}
      />
    );

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
        position: 'relative',
      }}
    >
      {errorMessage && (
        <Alert
          severity="error"
          onClose={() => setErrorMessage(null)}
          sx={{
            position: 'absolute',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2000,
            minWidth: 320,
          }}
        >
          {errorMessage}
        </Alert>
      )}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
        }}
      >
        <MapView
          positions={mapData.positions}
          devices={mapData.devices}
          selectedDeviceId={selectedDeviceId}
          onDeviceSelect={handleDeviceSelect}
          positionHistory={mapData.positionHistory}
        />
      </Box>
      {sidebarOpen && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            height: 'calc(100% - 32px)',
            width: 320,
            zIndex: 1000,
            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            borderRadius: '8px',
            overflow: 'hidden',
            ...(isMobile && {
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              borderRadius: 0,
            }),
          }}
        >
          <TraccarSidebar
            devices={mapData.devices}
            positions={mapData.positions}
            groups={appData.groups}
            selectedDeviceId={selectedDeviceId}
            onDeviceSelect={handleDeviceSelect}
            loading={appData.loading}
            onSettingsClick={handleSettingsClick}
            onReportsClick={handleReportsClick}
          />
        </Box>
      )}
      {!(isMobile && sidebarOpen) && (
        <Tooltip title={sidebarOpen ? 'Cerrar panel' : 'Abrir panel'}>
          <IconButton
            onClick={() => setSidebarOpen((v) => !v)}
            sx={{
              position: 'absolute',
              top: 16,
              left: sidebarOpen ? (isMobile ? 16 : 352) : 16,
              zIndex: 1500,
              bgcolor: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              '&:hover': { bgcolor: '#f5f5f5' },
              transition: 'left 0.3s ease-in-out',
            }}
          >
            {sidebarOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

IntegratedTraccarView.propTypes = {
  email: PropTypes.string,
  password: PropTypes.string,
};

export default IntegratedTraccarView;
